import { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Field } from "@/components/site/Form";
import { useJourney } from "@/lib/site";
import { api, errMsg, fieldErrors } from "@/lib/api";
import { SLOTS } from "@/lib/options";
import { PhotosStep } from "./PhotosStep";

const REVIEW_SLOTS = SLOTS.filter((x) => ["front", "rear", "left", "right", "interior", "damage"].includes(x.id)).map((x) => ({ ...x, multiple: false }));

const ReviewPhotos = ({ onBusy }) => (
  <div className="mt-8 border-t border-border pt-6" data-testid="review-photos">
    <h2 className="text-lg font-bold text-slate-900">Add photos <span className="font-normal text-slate-500">(optional)</span></h2>
    <p className="mt-1 text-sm text-slate-700">Up to six photos help us value your car more quickly. Take them with your phone camera or choose from your library. We'll only use them to assess your vehicle. You can skip this.</p>
    <PhotosStep compact slots={REVIEW_SLOTS} onBusy={onBusy} />
  </div>
);

const Input = ({ id, label, error, ...rest }) => (
  <Field label={label} htmlFor={id} error={error}>
    <input id={id} className={`field-input ${error ? "field-error" : ""}`} aria-invalid={!!error} {...rest} />
  </Field>
);

export const ContactStep = () => {
  const j = useJourney();
  const nav = useNavigate();
  const s = j.state;
  const c = s.contact || {};
  const [errors, setErrors] = useState({});
  const [formErr, setFormErr] = useState(null);
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);
  const manual = s.valuation?.status !== "ok";
  const inflight = useRef(false);
  const set = (k, v) => j.update({ contact: { ...c, [k]: v } });

  const validate = () => {
    const e = {};
    if (!c.name || c.name.trim().length < 2) e.name = "Please enter your full name.";
    if (!/^\+?\d{10,13}$/.test((c.phone || "").replace(/[^\d+]/g, ""))) e.phone = "Please enter a valid UK phone number.";
    if (!c.email || !/^[^@\s]+@[^@\s]+\.[A-Za-z]{2,}$/.test(c.email.trim())) e.email = "Please enter a valid email address.";
    if (!/^[A-Z]{1,2}[0-9][A-Z0-9]?\s*[0-9][A-Z]{2}$/.test((c.postcode || "").trim().toUpperCase())) e.postcode = "Please enter your full UK postcode, for example WD7 9EN.";
    setErrors(e);
    return !Object.keys(e).length;
  };

  const submit = async (ev) => {
    ev.preventDefault();
    if (inflight.current || !validate()) return;
    if (!s.draft) return setFormErr("Your enquiry is still being prepared. Please try again in a moment.");
    if (uploading) return setFormErr("Please wait for your photos to finish uploading.");
    inflight.current = true;
    setBusy(true);
    setFormErr(null);
    const src = s.manual || s.valuation;
    try {
      const { data } = await api.post("/public/enquiries", {
        draft_id: s.draft.id, draft_token: s.draft.token, valuation_id: s.valuation?.valuation_id || null,
        registration: src.registration, mileage: src.mileage, intent: "sell",
        vehicle: { comments: s.comments || null },
        contact: { name: c.name, phone: c.phone, email: c.email?.trim(), postcode: c.postcode },
        source: sessionStorage.getItem("hcs_src") || null,
      });
      j.update({ submitted: { reference: data.reference, is_test: !!data.is_test || src.mode === "sandbox" } });
      nav("/sell/sent", { replace: true });
    } catch (e) {
      setErrors(fieldErrors(e));
      setFormErr(errMsg(e, "We couldn't send your enquiry. Please try again – you won't create a duplicate."));
    } finally {
      inflight.current = false;
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} noValidate data-testid="contact-step">
      <h1 className="text-2xl font-extrabold text-slate-900 sm:text-3xl">Your contact details</h1>
      <p className="mt-2 text-slate-700">We'll use these to contact you about your car.</p>
      <div className="mt-6 grid gap-5 sm:grid-cols-2 sm:items-start">
        <div className="sm:col-span-2"><Input id="name" label="Full name" autoComplete="name" value={c.name || ""} onChange={(e) => set("name", e.target.value)} error={errors.name} maxLength={120} data-testid="contact-name-input" /></div>
        <div className="sm:col-span-2"><Input id="email" label="Email address" type="email" inputMode="email" autoComplete="email" value={c.email || ""} onChange={(e) => set("email", e.target.value)} error={errors.email} maxLength={200} data-testid="contact-email-input" /></div>
        <Input id="phone" label="Telephone number" type="tel" inputMode="tel" autoComplete="tel" value={c.phone || ""} onChange={(e) => set("phone", e.target.value)} error={errors.phone} maxLength={30} data-testid="contact-phone-input" />
        <Input id="postcode" label="Postcode" autoComplete="postal-code" autoCapitalize="characters" value={c.postcode || ""} onChange={(e) => set("postcode", e.target.value.toUpperCase())} error={errors.postcode} maxLength={10} data-testid="contact-postcode-input" />
        <div className="sm:col-span-2">
          <Field label="Anything else we should know about your car?" htmlFor="comments" optional>
            <textarea id="comments" rows={3} maxLength={2000} className="field-input" value={s.comments || ""} onChange={(e) => j.update({ comments: e.target.value })} data-testid="contact-comments-input" />
          </Field>
        </div>
      </div>
      {manual && <ReviewPhotos onBusy={setUploading} />}
      <p className="mt-6 text-sm text-slate-600" data-testid="submit-notice">
        Sending your enquiry doesn't confirm a sale or commit you to anything. See our <a className="link" href="/privacy" target="_blank" rel="noreferrer">privacy information</a>.
      </p>
      {formErr && <p className="mt-4 text-sm font-medium text-destructive" role="alert" data-testid="submit-error">{formErr}</p>}
      <Button type="submit" size="lg" className="btn-cta mt-6 h-12 w-full text-base sm:w-auto sm:px-8" disabled={busy} data-testid="submit-enquiry-button">
        {busy ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Sending…</> : "Send My Enquiry"}
      </Button>
    </form>
  );
};
